import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Allocation } from "../services/allocation";

export function useGetAllocationsByProject(projectId: string) {
  return useQuery({
    queryKey: ["allocationGroups", projectId],
    queryFn: () => Allocation.getByProject(projectId),
    enabled: !!projectId,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

export function useGetAllAllocations(projectId?: string) {
  return useQuery({
    queryKey: ["allocations", "all", projectId],
    queryFn: () => Allocation.getAll(projectId),
    enabled: true,
  });
}

export function useGetAllocationById(allocationId: string) {
  return useQuery({
    queryKey: ["allocations", "single", allocationId],
    queryFn: () => Allocation.getById(allocationId),
    enabled: !!allocationId,
  });
}

export function useCreateAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Allocation.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
    },
  });
}

export function useUpdateAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ allocationId, data }: { allocationId: string; data: any }) =>
      Allocation.update(allocationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
      queryClient.invalidateQueries({ queryKey: ["allocationGroups"] });
    },
  });
}

export function useDeleteAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Allocation.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ allocationId, userId }: { allocationId: string; userId: string }) =>
      Allocation.assignToUser(allocationId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
      queryClient.invalidateQueries({ queryKey: ["allocationGroups"] });
    },
  });
}

export function useUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ allocationId, status }: { allocationId: string; status: string }) =>
      Allocation.updateStatus(allocationId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
    },
  });
}

// Form Data Hooks
export function useSubmitFormData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ allocationId, formData, status }: { allocationId: string; formData: any, status: string }) =>
      Allocation.submitFormData(allocationId, formData, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocations"] });
    },
  });
}

export function useGetFormData(allocationId: string) {
  return useQuery({
    queryKey: ["form", allocationId],
    queryFn: () => Allocation.getFormData(allocationId),
    enabled: !!allocationId,
  });
}

// Allocation Logs Hooks
export function useGetAllocationLogs(allocationId: string) {
  return useQuery({
    queryKey: ["allocation-logs", allocationId],
    queryFn: () => Allocation.getLogs(allocationId),
    enabled: !!allocationId,
  });
}

export function useGetProjectLogs(projectId: string) {
  return useQuery({
    queryKey: ["allocation-logs", "project", projectId],
    queryFn: () => Allocation.getProjectLogs(projectId),
    enabled: !!projectId,
  });
}

export function useCreateLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: Allocation.createLog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allocation-logs"] });
    },
  });
}

// Get comprehensive allocation details
export function useGetAllocationDetails(allocationId: string) {
  return useQuery({
    queryKey: ["allocation-details", allocationId],
    queryFn: () => Allocation.getDetails(allocationId),
    enabled: !!allocationId,
  });
}